import { useEffect, useMemo, useState } from 'react';

import { api } from '../api/client';
import { API_ITEMS_URL } from '../utils';
import { useOptionalCardDefinitions } from '../contexts/CardDefinitionsContext';
import { useCollectionContext } from '../contexts/CollectionContext';
import { getCollectionMemberCardIds } from '../utils/collections';
import { normalizeId } from '../utils/ids';
import { ELEMENT_ID_RE, cmp, list, or, serialize, str } from '../utils/kql';
import { identityCardsOf, userIdsOf } from '../utils/userElements';
import { onSessionChange } from '../utils/session';

/**
 * The items a collection LINKS to a set of accounts — what a user value
 * renders as when the collection has a people card with an account link
 * (docs/plans/element-sets-and-me.md, "Display of a User value"): Molly's
 * assignee chip becomes Molly's staff record badge, and the door to it.
 *
 * The API has no "items for these accounts" endpoint, so this is one KQL
 * list query per batch over the identity elements of the collection's
 * cards: `GET /api/items?collection_id=…&q=<link> in (id, id, …)` (an `or`
 * across links when several cards carry one). The answer's `accountId`
 * (server-managed) is the key back.
 *
 * Batched and cached per collection for the session: a table of fifty
 * assignee cells asks once, and an id asked about is never asked again
 * (an account with no record is remembered as null). `refreshLinkedAccounts`
 * forgets a collection — for a surface that just linked an item. A
 * collection whose cards carry no link never fetches.
 *
 * Returns `{ linkedItemFor(userId) -> item | null, loading }`.
 */

const BATCH_LIMIT = 100;
// A query this long is one URL; the API's own `MAX_CONDITIONS` is far above it.
const IDS_PER_QUERY = 50;

// collectionId -> { itemByUser: Map<id, item|null>, pending: Set<id>, queue: Set<id>, timer, subs: Set<fn> }
const stores = new Map();

function storeFor(collectionId) {
	if (!stores.has(collectionId)) {
		stores.set(collectionId, { itemByUser: new Map(), pending: new Set(), queue: new Set(), timer: null, subs: new Set() });
	}
	return stores.get(collectionId);
}

/** Test seam / after linking: forget one collection's answers (all when omitted). */
export function refreshLinkedAccounts(collectionId = null) {
	if (!collectionId) { stores.clear(); return; }
	const store = stores.get(normalizeId(collectionId));
	if (!store) return;
	store.itemByUser.clear();
	store.pending.clear();
	store.queue.clear();
	store.subs.forEach(fn => fn());
}

// The linked items are what THIS account may read: another account (or
// none) forgets every collection's answers, and mounted cells re-ask.
onSessionChange(() => { [...stores.keys()].forEach(id => refreshLinkedAccounts(id)); });

/** The query that finds the items linked to `ids` through `linkNames`. */
export function linkedAccountsQuery(linkNames, ids) {
	const conditions = linkNames.map(name => cmp([name], 'in', list(ids.map(id => str(id)))));
	if (!conditions.length) return '';
	return serialize(conditions.length === 1 ? conditions[0] : or(conditions));
}

function flush(collectionId, linkNames) {
	const store = storeFor(collectionId);
	store.timer = null;
	const ids = [...store.queue].filter(id => !store.itemByUser.has(id) && !store.pending.has(id));
	store.queue.clear();
	if (!ids.length) return;
	ids.forEach(id => store.pending.add(id));
	const chunks = [];
	for (let i = 0; i < ids.length; i += IDS_PER_QUERY) chunks.push(ids.slice(i, i + IDS_PER_QUERY));
	Promise.all(chunks.map(chunk => api.get(API_ITEMS_URL, {
		params: { collection_id: collectionId, q: linkedAccountsQuery(linkNames, chunk), limit: BATCH_LIMIT },
	})
		.then(({ data }) => (Array.isArray(data) ? data : []))
		.catch(() => [])))
		.then(results => {
			const found = new Set();
			results.flat().forEach(item => {
				const accountId = typeof item?.accountId === 'string' ? item.accountId : null;
				if (!accountId) return;
				store.itemByUser.set(accountId, item);
				found.add(accountId);
			});
			ids.forEach(id => {
				store.pending.delete(id);
				if (!found.has(id)) store.itemByUser.set(id, null);
			});
			store.subs.forEach(fn => fn());
		});
}

function request(collectionId, linkNames, ids) {
	const store = storeFor(collectionId);
	let queued = false;
	ids.forEach(id => {
		if (store.itemByUser.has(id) || store.pending.has(id)) return;
		store.queue.add(id);
		queued = true;
	});
	if (queued && !store.timer) {
		// Every cell of one render lands in the same batch.
		store.timer = setTimeout(() => flush(collectionId, linkNames), 0);
	}
}

/**
 * `userIds` may be one id, a list, or a stored user value of either shape.
 * `enabled: false` (a public page, a collection still loading) asks nothing.
 */
export default function useLinkedAccounts(collectionId, userIds, { enabled = true } = {}) {
	const id = normalizeId(collectionId);
	const cardDefinitions = useOptionalCardDefinitions();
	const cards = cardDefinitions?.cards;
	const getElementsForCard = cardDefinitions?.getElementsForCard;
	// The card cache is app-wide (every readable collection's cards): inside
	// the collection's layout, only ITS member cards' links are asked about —
	// another collection's link name is not a link here, and one that
	// collides with a plain user element of this collection would match the
	// wrong items (and could crowd the linked records out of the batch).
	const context = useCollectionContext();
	const collection = context?.collection && normalizeId(context.collection._id) === id ? context.collection : null;

	// The identity elements the collection's cards carry — each by its id
	// (`#k7f3q2xa`; element ids step 3): two cards' account links of one name
	// are two elements, and a name several elements answer to is refused
	// (400 `ambiguous_element`), which this quiet read would swallow into
	// "nobody is linked". An element with no id yet is asked by its name.
	const linkNames = useMemo(() => {
		if (!cards?.length) return [];
		const cardIds = collection ? getCollectionMemberCardIds(collection, cards) : null;
		const names = new Set(identityCardsOf({ cards, cardIds, getElementsForCard })
			.map(({ element }) => (typeof element.id === 'string' && ELEMENT_ID_RE.test(element.id) ? `#${element.id}` : element.name)));
		return [...names].sort();
	}, [cards, collection, getElementsForCard]);
	const linkKey = linkNames.join(',');

	const ids = useMemo(() => userIdsOf(userIds), [userIds]);
	const idsKey = ids.join(',');

	const [, setVersion] = useState(0);

	useEffect(() => {
		if (!enabled || !id || !linkKey || !idsKey) return undefined;
		const store = storeFor(id);
		const bump = () => setVersion(v => v + 1);
		store.subs.add(bump);
		request(id, linkKey.split(','), idsKey.split(','));
		return () => { store.subs.delete(bump); };
	}, [enabled, id, linkKey, idsKey]);

	const store = id ? stores.get(id) : null;
	const loading = !!store && ids.some(userId => store.pending.has(userId) || store.queue.has(userId));

	return {
		linkedItemFor: userId => {
			const key = normalizeId(userId);
			if (!key || !store) return null;
			return store.itemByUser.get(key) || null;
		},
		loading: enabled && !!linkKey && loading,
		hasLinks: linkNames.length > 0,
	};
}
